import React, { useState } from 'react';
import ToastContainer from 'react-bootstrap/ToastContainer';
import Toast from 'react-bootstrap/Toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { STANDARD_ERROR_MESSAGE } from '@/constants/httpStatus';
import classes from './toast.context.module.scss';

const TOAST_ICONS = {
  success: ['fas', 'check'],
  warning: ['fas', 'triangle-exclamation'],
  error: ['fas', 'xmark'],
  info: ['fas', 'circle-info']
};

const TOAST_CLASS = {
  success: 'toastSuccess',
  warning: 'toastWarning',
  error: 'toastError',
  info: 'toastInfo'
};

export const toastContext = React.createContext({});

export const ToastProvider = ({ children }) => {
  const [toastQueue, setToastQueue] = useState([]);

  const checkForValidToasts = (toast) => {
    return typeof toast.show === 'undefined' || toast.show === true;
  };

  const addToast = (toastConfig) => {
    if (toastConfig.type === 'error' && !toastConfig.message) {
      toastConfig = { ...toastConfig, message: STANDARD_ERROR_MESSAGE };
    }
    setToastQueue((prevQueue) => {
      // to avoid leaking memory, purge state array if all toasts in it are hidden
      if (prevQueue.filter(checkForValidToasts).length === 0) {
        return [toastConfig];
      } else {
        return [...prevQueue, toastConfig];
      }
    });
  };

  // toasts must be hidden rather than removed from the state array to avoid Bootstrap losing track
  // of which Toasts still need to be auto-hidden
  const hideToast = (indexToHide) => {
    setToastQueue((prevQueue) =>
      prevQueue.map((toast, index) => {
        if (index === indexToHide) {
          toast.show = false;
        }
        return toast;
      })
    );
  };

  const displayContent = (withHtml, message) => {
    if (withHtml) {
      return <div dangerouslySetInnerHTML={{ __html: message }}></div>;
    } else {
      return message;
    }
  };

  return (
    <toastContext.Provider value={{ addToast }}>
      <>
        <div key="toastSibling">{children}</div>
        <ToastContainer className={classes.toastContainer}>
          {toastQueue.map(({ header, message, withHtml, delay = 5000, autohide = true, timestamp, show = true, type = 'info' }, index) => {
            return (
              <Toast
                className={classes.toastInnerContainer}
                key={index}
                show={show}
                delay={delay}
                autohide={autohide}
                onClose={(e) => {
                  hideToast(index);
                }}
              >
                {header && <Toast.Header>{header}</Toast.Header>}
                {message && (
                  <Toast.Body data-cy="toast" className={`${classes.toastBody} ${classes[TOAST_CLASS[type]]}`}>
                    {TOAST_ICONS[type] && <FontAwesomeIcon icon={TOAST_ICONS[type]} className={classes.toastIcon} />}
                    <span className={classes.toastMessage}>{displayContent(withHtml, message)}</span>
                    <button className={classes.toastDismiss} onClick={() => hideToast(index)} aria-label="Dismiss">
                      <FontAwesomeIcon icon={['fas', 'xmark']} />
                    </button>
                  </Toast.Body>
                )}
              </Toast>
            );
          })}
        </ToastContainer>
      </>
    </toastContext.Provider>
  );
};

export const ToastConsumer = toastContext.Consumer;
